import { LOCATION_CHANGE, replace } from 'redux-first-history';
import { call, getContext, put, select, takeEvery } from 'redux-saga/effects';
import { OAuth2CodeWrongOrExpiredError } from 'src/features/oauth2';
import { trackError } from 'src/lib/appError';
import { container } from 'src/StaticContainer';
import type { RootState } from 'src/store';
import { setAccessToken, setAuthError, setIdToken, setRefreshToken } from 'src/store/actions/auth';
import type { SagaContext } from 'src/store/types';

const log = container.get('Logger').getSubLogger({ name: 'store:sagas:auth:oauth2Callback' }, { component: 'auth' });

// Track state mismatch recovery attempts to prevent infinite retry loops.
// Reset on successful login callback.
let stateMismatchRecoveryAttempted = false;

/** @internal Exported for testing only */
export function resetStateMismatchRecovery() {
    stateMismatchRecoveryAttempted = false;
}

// From: https://datatracker.ietf.org/doc/html/rfc6749#section-4.1.2.1
enum RFC6749ResponseError {
    InvalidRequest = 'invalid_request',
    UnauthorizedClient = 'unauthorized_client',
    AccessDenied = 'access_denied',
    UnsupportedResponseType = 'unsupported_response_type',
    InvalidScope = 'invalid_scope',
    ServerError = 'server_error',
    TemporarilyUnavailable = 'temporarily_unavailable',
}

const RFC6749ResponseErrorMessages: Record<RFC6749ResponseError, string> = {
    [RFC6749ResponseError.InvalidRequest]: 'There was a communication error. (InvalidRequest)',
    [RFC6749ResponseError.UnauthorizedClient]: 'The credentials you have entered are invalid.',
    [RFC6749ResponseError.AccessDenied]: 'The credentials you have entered are invalid.',
    [RFC6749ResponseError.UnsupportedResponseType]: 'There was a communication error. (UnsupportedResponseType)',
    [RFC6749ResponseError.InvalidScope]: 'There was a communication error. (InvalidScope)',
    [RFC6749ResponseError.ServerError]: 'There was a communication error. (ServerError)',
    [RFC6749ResponseError.TemporarilyUnavailable]: 'Login is currently unavailable. Please try again later.',
};

export function* listenForCallback() {
    if (APP_ELECTRON) {
        yield takeEvery(LOCATION_CHANGE, handleCallback);
    } else {
        yield* handleCallback();
    }
}

export function* handleCallback() {
    const location: RootState['router']['location'] = yield select((state: RootState) => state.router.location);

    if (location?.pathname !== '/oauth2/callback') return;

    const query = new URLSearchParams(location.search);

    const error = query.get('error') as RFC6749ResponseError | null;
    if (error) {
        const error_reason = query.get('error_reason');
        const error_description = query.get('error_description');

        // Always track the error.
        trackError(new Error(`RFC6749ResponseError: ${error}`), {
            extra: {
                error: error,
                error_reason: error_reason ?? '',
                error_description: error_description ?? '',
            },
        });

        // If we have a message for the error, show that to the user.
        if (Object.values(RFC6749ResponseError).includes(error)) {
            yield put(setAuthError(RFC6749ResponseErrorMessages[error]));
        }

        return;
    }

    const code = query.get('code');
    if (!code) {
        trackError(new Error('No code during authentication flow'), { extra: query });
        yield put(setAuthError('No code detected.'));
        return;
    }

    const oauth2: SagaContext['oAuth2Client'] = yield getContext('oAuth2Client');

    try {
        const { state: flowState, tokens } = yield oauth2.loginCallback(location.search);

        // Successful callback - reset recovery flag
        stateMismatchRecoveryAttempted = false;

        if (TRACE) {
            log.trace({
                message: 'oauth2Callback: managed loginCallback result',
                hasAccessToken: typeof tokens.accessToken === 'string',
                accessTokenLength: typeof tokens.accessToken === 'string' ? tokens.accessToken.length : 0,
            });
        }
        yield put(setAccessToken({ token: tokens.accessToken, expires: undefined }));
        if (tokens.idToken) yield put(setIdToken({ token: tokens.idToken, expires: undefined }));
        if (tokens.refreshToken) yield put(setRefreshToken({ token: tokens.refreshToken, expires: undefined }));

        // Determine where to redirect the user
        const defaultRedirect = '/dashboard';
        const blockedPaths = ['/logout', '/loggedout', '/login', '/register'];

        // Only use returnTo from state if it exists and is not a blocked path
        const returnTo =
            flowState.returnTo && !blockedPaths.includes(flowState.returnTo) ? flowState.returnTo : defaultRedirect;

        yield put(replace(returnTo));
    } catch (error: unknown) {
        if (error instanceof Error) {
            log.error('Error during OAuth2 callback', { error });
            trackError(error, { extra: { error: error } });

            // Auto-recover from OAuth state mismatch by restarting the login flow.
            // This happens when sync-storage contains stale OIDC state from a previous session.
            // loginCallback() has already cleared the stale state, so restarting should succeed.
            if (error.message.includes('State not valid') && !stateMismatchRecoveryAttempted) {
                stateMismatchRecoveryAttempted = true;
                log.warn('Auto-recovering from OAuth state mismatch - restarting login flow');
                try {
                    yield call([oauth2, oauth2.startOauth]);
                    return;
                } catch (recoveryError) {
                    log.error('Failed to restart login flow during state mismatch recovery', {
                        error: recoveryError,
                    });
                    // Fall through to normal error handling below
                }
            }

            yield put(replace(location.pathname));

            if (error instanceof OAuth2CodeWrongOrExpiredError) {
                yield put(setAuthError(RFC6749ResponseErrorMessages[RFC6749ResponseError.InvalidRequest]));
            } else {
                yield put(setAuthError(error.message));
            }
        } else {
            yield put(setAuthError('An unknown authentication error has occurred.'));
        }
    }
}
